import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { safeHref } from '../../utils/url';

// Exchange panel shown below map — collapsible, compact on mobile, full cards on desktop.
// Used by both BusinessMapCountry and BusinessMapRegion.
//
// IMPORTANT: render the header bar even before the exchanges array arrives
// (exchanges === null = still loading). Previously this component returned
// null until the fetch completed, which meant iOS users couldn't see the
// bar at all on initial paint — they'd rotate/resize the device and it'd
// finally appear "empty". Now the bar is always visible with a loading
// or count state; the panel body only populates when data lands.
const ExchangesPanel = ({ exchanges }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const loading = exchanges === null || exchanges === undefined;
  const list = Array.isArray(exchanges) ? exchanges : [];
  return (
    <div className="bg-background border-t border-border px-4 py-3 flex-shrink-0">
      <button
        onClick={() => setPanelOpen(prev => !prev)}
        className="flex items-center gap-2 w-full text-left group"
        aria-expanded={panelOpen}
      >
        {panelOpen ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
        <h2 className="text-xs md:text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
          Bitcoin Exchanges in this region
          <span className="ml-1.5 text-[10px] md:text-xs font-normal text-muted-foreground">
            {loading ? '(…)' : `(${list.length})`}
          </span>
        </h2>
      </button>
      {panelOpen && (
        <div className="mt-3 max-h-[40vh] overflow-y-auto">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading exchanges…</p>
          ) : list.length === 0 ? (
            <p className="text-xs text-muted-foreground">No exchanges listed for this region yet.</p>
          ) : (
            <div className="flex flex-col md:flex-wrap md:flex-row gap-1 md:gap-4">
              {list.map(ex => {
                const logoSrc = ex.logo_path ? ex.logo_path.replace('/uploads/', '/api/uploads/') : null;
                const isExpanded = expandedId === ex.id;
                const exHref = safeHref(ex.url);
                // Shared card body so the desktop card can render as an <a>
                // (when there's a safe link) or a plain <div> (when there isn't).
                const cardInner = (
                  <>
                    <div className="flex-shrink-0 w-10 h-10 bg-muted rounded-md flex items-center justify-center overflow-hidden">
                      {logoSrc ? (
                        <img src={logoSrc} alt={ex.name} className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-lg">💱</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{ex.name}{ex.is_p2p_global && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded align-middle">Global P2P</span>}</p>
                      {ex.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{ex.description}</p>
                      )}
                    </div>
                  </>
                );
                return (
                  <div key={ex.id} className="w-full md:w-auto md:min-w-[200px] md:max-w-xs">
                    {/* Mobile compact row */}
                    <div className="flex md:hidden items-center gap-2 py-1.5 rounded-lg hover:bg-muted transition-colors">
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : ex.id)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        aria-expanded={isExpanded}
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-muted rounded overflow-hidden flex items-center justify-center">
                          {logoSrc ? (
                            <img src={logoSrc} alt={ex.name} className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-xs">💱</span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-foreground truncate">{ex.name}{ex.is_p2p_global && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">P2P</span>}</span>
                      </button>
                      {exHref && (
                        <a
                          href={exHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 text-muted-foreground hover:text-primary px-1"
                          aria-label={`Visit ${ex.name}`}
                          onClick={e => e.stopPropagation()}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      )}
                    </div>
                    {/* Mobile expanded description */}
                    {isExpanded && ex.description && (
                      <p className="md:hidden text-xs text-muted-foreground pl-8 pb-1.5">{ex.description}</p>
                    )}
                    {/* Desktop full card — link only when there's a safe URL */}
                    {exHref ? (
                      <a
                        href={exHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden md:flex items-center gap-3 hover:bg-muted rounded-lg p-2 transition-colors"
                      >
                        {cardInner}
                      </a>
                    ) : (
                      <div className="hidden md:flex items-center gap-3 rounded-lg p-2">
                        {cardInner}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExchangesPanel;
